import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { FormikProps, useFormik } from 'formik';
import { ReactElement, useCallback } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import MunicipalityAutoComplete from 'system/components/shared/search-municipality';
import { MunicipalityVM } from 'system/view-models/municipality';
import * as yup from 'yup';
import ConsulateCategories from './categories';
import { TerritoryVM } from './territory';
import PmiTextField from 'shell/components/shared/text-field';
import { regexOnlyNumber } from 'shell/constants/regex';

export interface ConsulateForm {
  code?: string;
  name?: string;
  category?: string;
  address?: string;
  municipality?: MunicipalityVM | null;
  territory?: Array<TerritoryVM>;
}

const validationSchema = yup.object().shape({
  code: yup
    .string()
    .matches(regexOnlyNumber, 'Il campo è numerico')
    .required('Il campo codice consolato è obbligatorio'),
  name: yup.string().required('Il campo nome consolato è obbligatorio'),
  category: yup.string().required('Il campo categoria è obbligatorio'),
  address: yup.string().required('Il campo indirizzo è obbligatorio'),
  municipality: yup
    .object()
    .required('Il campo comune sede consolare è obbligatorio')
    .nullable(true),
  territory: yup
    .array()
    .of(
      yup.object().shape({
        code: yup.string(),
      })
    )
    .min(1),
});

interface FormConsulateProps {
  onOk: (form: ConsulateForm) => void;
  onCancel: () => void;
  title: string;
  children?: ((formConsulate?: FormikProps<ConsulateForm>) => ReactElement) | never | never[];
  disabled?: boolean;
  initialValues?: ConsulateForm;
  isEdit?: boolean;
}

const FormConsulate = ({
  onOk,
  onCancel,
  title,
  children,
  disabled = false,
  initialValues,
  isEdit = false,
}: FormConsulateProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formConsulate = useFormik<ConsulateForm>({
    initialValues: initialValues || {
      code: '',
      name: '',
      category: '',
      address: '',
      municipality: null,
      territory: [],
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formConsulate.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice consolato"
                  label="Codice consolato *"
                  id="code"
                  fullWidth
                  name="code"
                  type="text"
                  inputProps={{
                    maxLength: '7',
                  }}
                  variant="outlined"
                  value={formConsulate.values.code || ''}
                  disabled={disabled || isEdit}
                  onChange={formConsulate.handleChange}
                  error={formConsulate.touched.code && Boolean(formConsulate.errors.code)}
                  helperText={formConsulate.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <PmiTextField
                  aria-label="nome consolato"
                  label="Nome consolato *"
                  id="name"
                  name="name"
                  fullWidth
                  type="text"
                  inputProps={{
                    maxLength: '100',
                  }}
                  variant="outlined"
                  value={formConsulate.values.name || ''}
                  disabled={disabled}
                  onChange={formConsulate.handleChange}
                  error={formConsulate.touched.name && Boolean(formConsulate.errors.name)}
                  helperText={formConsulate.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ConsulateCategories
                  disabled={disabled}
                  value={formConsulate.values.category || ''}
                  onChange={formConsulate.handleChange}
                  error={formConsulate.touched.category && Boolean(formConsulate.errors.category)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <PmiTextField
                  aria-label="Indirizzo"
                  label="Indirizzo *"
                  id="address"
                  name="address"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '100',
                  }}
                  value={formConsulate.values.address || ''}
                  disabled={disabled}
                  onChange={formConsulate.handleChange}
                  error={formConsulate.touched.address && Boolean(formConsulate.errors.address)}
                  helperText={formConsulate.errors.address}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MunicipalityAutoComplete
                  selectedValue={formConsulate.values.municipality || { description: '' }}
                  handleChange={(value) => formConsulate.setFieldValue('municipality', value)}
                  label="Comune sede consolare"
                  required
                  params={{
                    italia: false,
                  }}
                  disabled={disabled}
                  error={
                    formConsulate.touched.municipality && Boolean(formConsulate.errors.municipality)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                {typeof children === 'function' ? children(formConsulate) : undefined}
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton
            variant="contained"
            color="primary"
            type="submit"
            disabled={disabled || !formConsulate.isValid}
          >
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormConsulate.displayName = 'Form Consulate component';

export default FormConsulate;
